import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { staffApi } from "@client/api/staff";
import { StaffShell } from "@client/components/StaffShell";
import { Badge } from "@client/components/ui/badge";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Label } from "@client/components/ui/label";
import { sessionStatusLabel } from "@client/lib/session-status";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { user } = Route.useRouteContext();
  const { data } = useQuery({
    queryKey: ["dashboard"],
    queryFn: staffApi.dashboard,
  });

  const stats = [
    { label: "Questions in bank", value: data?.questions ?? 0 },
    { label: "Exams defined", value: data?.exams ?? 0 },
    { label: "Graded attempts", value: data?.results ?? 0 },
  ];

  return (
    <StaffShell
      title="Dashboard"
      subtitle="Examination control overview"
      actions={
        <Button asChild size="sm">
          <Link to="/sessions">Manage sessions</Link>
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <div key={s.label} className="panel p-5">
            <p className="text-sm text-muted-foreground">{s.label}</p>
            <p className="hall-heading mt-2 text-4xl font-bold text-foreground">
              {s.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="panel mt-6 p-5">
          <h2 className="hall-heading text-lg font-bold">Recent sessions</h2>
          <div className="mt-4 space-y-2">
            {(data?.sessions ?? []).map((s) => (
              <Link
                key={s.id}
                to="/sessions/$sessionId"
                params={{ sessionId: s.id }}
                className="flex items-center justify-between rounded-md border border-border px-4 py-3 hover:bg-secondary"
              >
                <span className="font-medium text-foreground">{s.title}</span>
                <Badge variant="secondary">
                  {sessionStatusLabel(s.status)}
                </Badge>
              </Link>
            ))}
            {!data?.sessions.length ? (
              <p className="text-sm text-muted-foreground">
                No sessions created yet.
              </p>
            ) : null}
          </div>
        </div>

        {user.role === "admin" && (
          <div className="panel mt-6 p-5">
            <h2 className="hall-heading text-lg font-bold">
              Create Instructor
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Provision a new instructor account for examination control.
            </p>
            <CreateInstructorForm />
          </div>
        )}
      </div>
    </StaffShell>
  );
}

function CreateInstructorForm() {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");

  const createMutation = useMutation({
    mutationFn: staffApi.createInstructor,
    onSuccess: () => {
      toast.success("Instructor account created successfully.");
      setEmail("");
      setFullName("");
      setPassword("");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create instructor.");
    },
  });

  return (
    <form
      className="mt-4 space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        createMutation.mutate({ email, fullName, password });
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="inst-email">Email</Label>
        <Input
          id="inst-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="inst-name">Full Name</Label>
        <Input
          id="inst-name"
          required
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="inst-password">Password</Label>
        <Input
          id="inst-password"
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      <Button type="submit" disabled={createMutation.isPending}>
        {createMutation.isPending ? "Creating..." : "Create Instructor"}
      </Button>
    </form>
  );
}
